import { cache } from 'react';
import type {
    GfeCategoryMaintenanceResponse,
    GfeCategoryRequestOptions,
    GfeCategoryResponse
} from '@/types/gfe/category';
import { DEVICE } from '@/utils/constants';
import { getConfig } from '@/utils/config/config';
import { buildApiUrl, buildUrl } from '@/utils/url/build-url';
import { getCookieValue, COOKIES } from '@/utils/cookies';
import { fetchApi } from '../fetcher';

const toCommaList = (values?: string[]): string | undefined =>
    values && values.length > 0 ? values.join(',') : undefined;

export const getGfeCategoryParams = async (options: GfeCategoryRequestOptions = {}) => {
    const config = getConfig();
    const brand = process.env.BRAND as string;
    const product = process.env.PRODUCT as string;

    const region = await getCookieValue(COOKIES.REGION);
    const platform = (await getCookieValue(COOKIES.PLATFORM)) || DEVICE.MOBILE;

    const host = buildApiUrl(config.apis.GamingFusionEngineClient);
    const headers: Record<string, string> = {
        accept: 'application/json',
        'x-brand': brand
    };

    const { offset, limit, sort, provider, theme, genre, feature, aboveFold, includeProduct } = options;

    const rawParams: Record<string, string | number | boolean | undefined> = {
        product,
        state: region?.toLowerCase(),
        platform,
        offset,
        limit,
        sort,
        provider: toCommaList(provider),
        theme: toCommaList(theme),
        genre: toCommaList(genre),
        feature: toCommaList(feature),
        aboveFold,
        includeProduct
    };

    const params = Object.fromEntries(Object.entries(rawParams).filter(([, value]) => value !== undefined)) as Record<
        string,
        string | number | boolean
    >;

    return {
        host,
        headers,
        params,
        networkConfig: config.network.InternalGamingFusionEngine,
        timeout: config.apis.GamingFusionEngineClient.timeout
    };
};

export const getGfeCategory = async (
    categoryUid: string,
    options: GfeCategoryRequestOptions = {}
): Promise<GfeCategoryResponse | GfeCategoryMaintenanceResponse | null> => {
    const { host, headers, params, networkConfig, timeout } = await getGfeCategoryParams(options);
    const categoryUrl = `${host}/categories/${encodeURIComponent(categoryUid)}`;

    const init: RequestInit = { method: 'GET', headers };
    if (timeout) {
        init.signal = AbortSignal.timeout(timeout);
    }

    try {
        const res = await fetchApi(buildUrl(categoryUrl, params), init, { networkConfig });

        if (!res.ok) {
            // 4xx: expected misses that fall back to aggregation; 5xx: GFE faults.
            const log = res.status >= 500 ? console.error : console.warn;
            log(`(getGfeCategory) Non-OK response for ${categoryUid}: ${res.status}`);
            return null;
        }

        return (await res.json()) as GfeCategoryResponse | GfeCategoryMaintenanceResponse;
    } catch (error) {
        console.error(`(getGfeCategory) Error fetching category ${categoryUid}: ${(error as Error).message}`);
        return null;
    }
};

// Stable module reference so `cache` (keyed by `Object.is`) can dedup across calls.
const FULL_CATEGORY_OPTIONS: GfeCategoryRequestOptions = { aboveFold: 'all' };

// Request-scoped dedup: one GFE call per `categoryUid` per request.
export const getGfeCategoryCached = cache(
    (categoryUid: string): Promise<GfeCategoryResponse | GfeCategoryMaintenanceResponse | null> =>
        getGfeCategory(categoryUid, FULL_CATEGORY_OPTIONS)
);
